import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Stack, Text } from '@adc/parallax-component-library';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { SharingCodeErrorCode } from 'Enums';
import { useApiError, useLibrePartner } from 'Hooks';
import { Footer, Form, Input, Main, RichText, ScrollContainer, Spinner, TitleActionBar, } from 'Components/utility';
import { SHOW_MODAL } from 'Reducers/modal/types';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';
const MAX_LENGTH_CODE = 7;
const mapState = ({ connections: { availableCommercialAPIPartners } }) => {
    return {
        availableCommercialAPIPartners,
    };
};
const connector = connect(mapState);
const AddPartnerConnection = ({ availableCommercialAPIPartners }) => {
    const [loading, setLoading] = useState(false);
    const { sharingCodeValidate } = useLibrePartner();
    const { showApiErrorModal } = useApiError();
    useEffect(() => {
        if (!availableCommercialAPIPartners?.length) {
            mediator.publish('router:navigate', '/connections');
        }
    }, [availableCommercialAPIPartners]);
    const validationSchema = Yup.object().shape({
        code: Yup.string()
            .required(i18n.t('AddConnection.content.connectionFormFields.formField.code.errors.required'))
            .min(MAX_LENGTH_CODE, ''),
        birthYear: Yup.string()
            .required(i18n.t('AddConnection.content.connectionFormFields.formField.verifyBirthYear.errors.required'))
            .min(4, ''),
    });
    const displayMessageError = (modalProps) => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: modalProps,
        });
    };
    const handleErrorCode = (errorCode, reachedMaxYobAttempts) => {
        switch (errorCode) {
            case SharingCodeErrorCode.CODE_NOT_FOUND:
                displayMessageError({
                    header: i18n.t('AddConnection.modals.expiredCodeDialog.title'),
                    details: i18n.t('AddConnection.modals.expiredCodeDialog.body'),
                    testID: 'ExpiredCodeDialog',
                });
                break;
            case SharingCodeErrorCode.INVALID_YOB:
            case SharingCodeErrorCode.INCORRECT_YOB: {
                if (reachedMaxYobAttempts) {
                    displayMessageError({
                        header: i18n.t('AddConnection.modals.requestNewCodeDialog.title'),
                        details: i18n.t('AddConnection.modals.requestNewCodeDialog.body'),
                        testID: 'RequestNewCodeDialog',
                    });
                    return;
                }
                displayMessageError({
                    details: (React.createElement(RichText, { text: i18n.t('AddConnection.modals.mismatchedBirthYearDialog.body'), testID: "AddConnection.modals.mismatchedBirthYearDialog.body" })),
                    testID: 'MismatchedBirthYearDialog',
                });
                break;
            }
            default:
                showApiErrorModal();
        }
    };
    const onSubmit = async (values) => {
        try {
            setLoading(true);
            const { errorCode, reachedMaxYobAttempts, partnerId } = await sharingCodeValidate(values.code, Number(values.birthYear));
            if (errorCode || !partnerId) {
                handleErrorCode(errorCode, reachedMaxYobAttempts);
                return;
            }
            const availableCommercialApiPartner = availableCommercialAPIPartners?.find((p) => p.partner.id === partnerId);
            if (availableCommercialApiPartner) {
                const partnerConnection = {
                    partner: availableCommercialApiPartner.partner,
                    consent: {
                        ...availableCommercialApiPartner.consent,
                    },
                };
                mediator.publish('router:navigate', {
                    url: '/partner-confirm-connection',
                    data: {
                        partnerConnection,
                        code: values.code,
                        yearOfBirth: Number(values.birthYear),
                    },
                });
            }
        }
        catch {
            showApiErrorModal();
        }
        finally {
            setLoading(false);
        }
    };
    const { handleSubmit, handleChange, handleBlur, isValid, errors, touched, values } = useFormik({
        initialValues: { code: '', birthYear: '' },
        onSubmit,
        validationSchema,
        validateOnBlur: true,
    });
    return (React.createElement(Main, null,
        React.createElement(TitleActionBar, { testID: "AddConnection", title: i18n.t('AddConnection.title') }),
        loading && React.createElement(Spinner, { testID: "Login" }),
        React.createElement(ScrollContainer, { marginTop: 0 },
            React.createElement(Form, null,
                React.createElement(Text, { testID: "AddConnection.subtitle", fontSize: "$bodyBase.default", fontWeight: "$bodyBase.default", color: "$text.100", marginBottom: "$2" }, i18n.t('AddConnection.subtitle')),
                React.createElement(Stack, null,
                    React.createElement(Input, { "aria-label": i18n.t('AddConnection.content.connectionFormFields.formField.code.label'), placeholder: i18n.t('AddConnection.content.connectionFormFields.formField.code.placeholder'), label: i18n.t('AddConnection.content.connectionFormFields.formField.code.label'), testID: "code", inputMode: "text", errorMessage: errors.code, isInvalid: errors.code && touched.code ? true : false, value: values.code, onChangeText: handleChange('code'), onBlur: handleBlur('code'), maxLength: MAX_LENGTH_CODE }),
                    React.createElement(Text, { testID: "AddConnection.formField.helpText", fontSize: "$bodySmall.default", fontWeight: "$bodySmall.default", color: "$text.60", marginTop: "$2", marginBottom: "$3" }, i18n.t('AddConnection.content.connectionFormFields.formField.code.helpText.uniqueCode')),
                    React.createElement(Input, { "aria-label": i18n.t('AddConnection.content.connectionFormFields.formField.verifyBirthYear.label'), placeholder: i18n.t('AddConnection.content.connectionFormFields.formField.verifyBirthYear.placeholder'), label: i18n.t('AddConnection.content.connectionFormFields.formField.verifyBirthYear.label'), testID: "birthYear", errorMessage: errors.birthYear, isInvalid: errors.birthYear && touched.birthYear ? true : false, value: values.birthYear, onChangeText: (e) => {
                            const handler = handleChange('birthYear');
                            return handler(e.replace(/[^0-9]/g, ''));
                        }, onBlur: handleBlur('birthYear'), inputMode: "numeric", maxLength: 4 })))),
        React.createElement(Footer, { buttonText: i18n.t('Global.microcopy.common.next'), onButtonSubmit: handleSubmit, testIDButton: "AddConnection.inviteConnection-btn", isButtonDisabled: !values.code || !values.birthYear || !isValid })));
};
export default connector(AddPartnerConnection);
